import {
  type CanvasElement,
  type CanvasNamespaceId,
  type LayoutData,
} from '@blaze-runtime/engine-domain';
import { getConnectionNamespaceId } from '@blaze-runtime/engine-domain';
import { type PageInfo, type WorkflowsToRemoveInfo } from '@blaze-shared/api-contracts-domain';
import {
  type CanvasElementInputBinding,
  getInputConnections,
  type NavMenu,
} from '@blaze-shared/elements-domain';
import { isEqualNoUndefined } from '@blaze-shared/shared-util';
import { type StyleSheet } from '@blaze-shared/theming-domain';
import { type Connection } from '@blaze-shared/workflow-domain';

import { hasDataProps } from '../../../canvas-elements/element-model/element-props-guards';
import { type LayoutsApiDataRequest } from '../../../data/layouts-api.service';
import { type GetPropsExtractorFn } from '../../../services/element-definitions.service';
import { getStyleReferences } from '../../../services/styles';

import { getLayoutAccessRoles } from './get-layout-access-roles';
import { getLayoutPagesReferences } from './get-layout-pages-references';
import { getLayoutWorkflowsToRemove } from './get-layout-workflows-to-remove';

export interface LayoutSavingData {
  layouts: LayoutsApiDataRequest[];
  workflowsToRemove: WorkflowsToRemoveInfo[];
}

export interface CalcLayoutSaveDataParams {
  elements: CanvasElement[];
  connections: Connection[];
  layoutIds: CanvasNamespaceId[];
  layoutEntities: Record<CanvasNamespaceId, LayoutData>;
  offSyncLayouts: Record<CanvasNamespaceId, boolean>;
  navMenuItems: NavMenu.LegacySharedItem[];
  styleSheets: StyleSheet[];
  projectPages: PageInfo[] | null;
  hasUsersAccessRoles: boolean;
  getPropsExtractor: GetPropsExtractorFn | null;
}

export class SaveLayoutCorruptionError extends Error {}

/**
 * Returns the data about which layers got modified, including just the layers (ui/action) that were modified.
 * @param elements list of current elements
 * @param connections list of current connections
 * @param layoutIds list of loaded layout ids
 * @param layoutEntities dictionary of loaded layouts (as they were loaded from the server)
 * @returns list of layouts that are to be saved (including the layers that have been modified only)
 */
// TODO: test
export function calcLayoutSaveData({
  elements,
  connections,
  layoutIds,
  layoutEntities,
  offSyncLayouts,
  navMenuItems,
  styleSheets,
  projectPages,
  hasUsersAccessRoles,
  getPropsExtractor,
}: CalcLayoutSaveDataParams): LayoutSavingData {
  // Split elements by layouts
  const elementsByLayout = reduceGroupBy(elements, (element) => element.ns);
  const connectionsByLayout = reduceGroupBy(connections, (connection) =>
    getConnectionNamespaceId(connection.id),
  );
  const navMenuItemsByLayout = reduceGroupBy(navMenuItems, (menuItem) => menuItem.ns);

  const layoutsSaveData: LayoutsApiDataRequest[] = [];
  for (const id of layoutIds) {
    if (offSyncLayouts[id]) {
      continue;
    }

    const layoutData: LayoutsApiDataRequest = {
      pageId: id,
      version: layoutEntities[id].version,
    };

    const savedLayoutProperties = layoutEntities[id].uiLayer?.properties;

    // Layout elements and connections on the client side
    const layoutElements = elementsByLayout[id] || [];
    const layoutConnections = connectionsByLayout[id] || [];
    const layoutNavMenuItems = navMenuItemsByLayout[id] || [];

    // Layout elements and connections as they're known to be saved on the server
    const savedLayoutElements = layoutEntities[id].uiLayer?.content;
    const savedLayoutConnections = layoutEntities[id].actionLayer?.connections;

    // Detect possible layout corruption - BL-3934
    if (isLayoutCorruptionLikely(layoutElements, layoutConnections, savedLayoutConnections)) {
      throw new SaveLayoutCorruptionError(
        `Layout corruption likely in layout ${id}. Layout has not been saved.`,
      );
    }

    // Compare layout
    if (savedLayoutProperties?.type === 'NavMenuItems') {
      if (!isEqualNoUndefined(layoutNavMenuItems, savedLayoutElements)) {
        layoutData.uiLayer = {
          content: layoutNavMenuItems as any,
          properties: savedLayoutProperties,
        };
      }
    } else if (!isEqualNoUndefined(layoutElements, savedLayoutElements)) {
      layoutData.uiLayer = {
        content: layoutElements,
        properties: savedLayoutProperties,
      };

      // This information is saved in aside entity
      // It is not returned by layout api
      layoutData.styles = getStyleReferences(layoutElements, styleSheets);
    }

    // Compare connections
    if (!isEqualNoUndefined(layoutConnections, savedLayoutConnections)) {
      layoutData.actionLayer = { connections: layoutConnections };
    }

    // Get all page/dialog ids, omitting not existing ones
    layoutData.references = getLayoutPagesReferences(
      id,
      layoutElements,
      projectPages,
      getPropsExtractor,
    );

    // Get elements' input workflows access restrictions
    layoutData.accessRoles = getLayoutAccessRoles(
      hasUsersAccessRoles,
      layoutElements,
      connections,
      getPropsExtractor,
    );

    // Add to results
    if (layoutData.uiLayer !== undefined || layoutData.actionLayer !== undefined) {
      layoutsSaveData.push(layoutData);
    }
  }

  const workflowsToRemove = getLayoutWorkflowsToRemove(
    layoutsSaveData,
    connectionsByLayout,
    layoutEntities,
  );

  return { layouts: layoutsSaveData, workflowsToRemove };
}

/** Detect possible layout corruption - BL-3934 */
function isLayoutCorruptionLikely(
  elements: CanvasElement[],
  connections: Connection[],
  layoutConnections: Connection[],
): boolean {
  // If there are some connections or the saved layout has no connections - the layout is probably OK
  if (connections.length > 0 || layoutConnections.length === 0) {
    return false;
  }

  // If connections are missing, check if there are elements which we're sure that should have connection to an input workflow
  // as the Input Workflow cannot be disconnected
  // TODO: actually, it can be by moving the table to another namespace (to/from header or dialog for example)
  return elements.some((element) => {
    let bindingKey: CanvasElementInputBinding | undefined;
    switch (element.type) {
      case 'table':
      case 'list':
        bindingKey = 'value';
        break;
      case 'dropdown':
        bindingKey = 'optionsList';
        break;
    }

    if (hasDataProps(element) && bindingKey) {
      const dataSource = element.props.data.find(
        (data) => data.enabled !== false && data.key === bindingKey,
      )?.dataSource;
      // If the main source is missing or not a workflow - continue
      if (!dataSource || dataSource.sourceType !== 'context') {
        return false;
      }

      const workflowId = dataSource.id;
      const inputConnections = getInputConnections(connections, element.id);
      return !inputConnections.some(
        ({ source }) => source.layerType === 'Workflow' && source.id === workflowId,
      );
    }

    return false;
  });
}

function reduceGroupBy<TSource, TKey extends string | number | symbol>(
  arr: TSource[],
  getKey: (item: TSource) => TKey,
): Record<TKey, TSource[]> {
  return arr.reduce(
    (acc, item) => {
      const key = getKey(item);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    },
    {} as Record<TKey, TSource[]>,
  );
}
